import axios from "axios";
import { decryptCryptoJs } from "@/Helper/crypto";

const LOGIN_KEY = process.env.REACT_APP_KEY_LOGIN_DATA;

let cachedAuth = {
  storageValue: null,
  token: null,
  userId: null,
};

const readAuthFromStorage = async () => {
  if (typeof window === "undefined" || !window?.localStorage || !LOGIN_KEY) {
    return { token: null, userId: null };
  }

  const stored = window.localStorage.getItem(LOGIN_KEY);
  if (!stored) return { token: null, userId: null };

  if (cachedAuth.storageValue === stored) {
    return { token: cachedAuth.token, userId: cachedAuth.userId };
  }

  try {
    const decrypted = await decryptCryptoJs(stored);
    const parsed = JSON.parse(decrypted);
    const token = parsed?.token || parsed?.jwtToken || null;
    const userId = parsed?.userId ?? parsed?.id ?? null;

    cachedAuth = {
      storageValue: stored,
      token,
      userId,
    };

    return { token, userId };
  } catch {
    cachedAuth = { storageValue: stored, token: null, userId: null };
    return { token: null, userId: null };
  }
};

const getPathname = (url = "") => {
  if (!url) return "";
  try {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return new URL(url).pathname;
    }
  } catch {
    // ignore
  }
  return url.split("?")[0];
};

const isPublicRoute = (method = "get", url = "") => {
  const httpMethod = method.toLowerCase();
  const pathname = getPathname(url);

  if (httpMethod === "post" && (pathname === "/auth/login" || pathname === "/auth/register")) {
    return true;
  }

  if (httpMethod !== "get") return false;

  if (pathname === "/categories") return true;
  if (pathname === "/players") return true;
  if (/^\/players\/[^/]+\/dashboard$/.test(pathname)) return true;
  if (/^\/rankings\/[^/]+$/.test(pathname)) return true;
  if (/^\/rankings\/[^/]+\/standings$/.test(pathname)) return true;

  return false;
};

export function createHttpClient(baseURL, extraConfig = {}) {
  const client = axios.create({
    baseURL,
    timeout: 60000,
    ...extraConfig,
  });

  client.interceptors.request.use(async (config) => {
    const shouldSkipAuth = isPublicRoute(config?.method, config?.url);
    if (shouldSkipAuth) return config;

    const { token } = await readAuthFromStorage();
    if (!token) return config;

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error(`[API ERROR - ${baseURL}]`, error);
      return Promise.reject(error);
    }
  );

  return client;
}
